import React from 'react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import {
  useVirtualTourAddonsQuery,
  useVirtualTourMediaQuery,
  type TourDimensions,
} from '@/client/api/listing-management/virtual-tours.query';
import {
  useImportTourMediaMutation,
  useOrderTourAddonMutation,
  useSetEmbedOptionsMutation,
} from '@/client/api/listing-management/virtual-tours.mutation';
import { getApiErrorMessage } from '@/client/utils/errors';
import { ActionDialog } from './ActionDialog';

// ─── Player settings ─────────────────────────────────────────────────────────

const onOff = (on: string, off: string): [string, string][] => [['1', on], ['0', off]];
const PLAYER_FIELDS: { key: string; label: string; options: [string, string][] }[] = [
  { key: 'brand', label: 'Matterport branding', options: onOff('Shown', 'Hidden') },
  { key: 'help', label: 'Help overlay on load', options: [['0', 'Off'], ['1', 'On'], ['2', 'Only first visit']] },
  { key: 'qs', label: 'Quickstart (skip fly-in)', options: onOff('On', 'Off') },
  { key: 'title', label: 'Space title', options: onOff('Shown', 'Hidden') },
  { key: 'vr', label: 'VR button', options: onOff('Shown', 'Hidden') },
  { key: 'dh', label: 'Dollhouse', options: onOff('Enabled', 'Disabled') },
  { key: 'mls', label: 'MLS mode (hide branding/links)', options: [['0', 'Off'], ['1', 'On'], ['2', 'On, with branding']] },
  { key: 'nozoom', label: 'Disable scroll zoom', options: onOff('Yes', 'No') },
  { key: 'play', label: 'Autoplay', options: onOff('On', 'Off') },
];

export const PlayerSettingsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  vtid: number;
  current: Record<string, unknown> | null | undefined;
}> = ({ open, onClose, vtid, current }) => {
  const m = useSetEmbedOptionsMutation();
  const [vals, setVals] = React.useState<Record<string, string>>({});
  React.useEffect(() => {
    if (!open) return;
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(current ?? {})) if (v != null) next[k] = String(v);
    setVals(next);
  }, [open, current]);
  const set = (k: string, v: string) => setVals((p) => ({ ...p, [k]: v }));
  const ts = vals.ts ?? '';
  const tsOk = ts === '' || (/^\d+$/.test(ts) && Number(ts) <= 300);
  const lang = vals.lang ?? '';
  const langOk = lang === '' || /^[a-z]{2}$/i.test(lang);
  const cls = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm';

  const options = () => {
    const out: Record<string, string | null> = {};
    for (const f of PLAYER_FIELDS) out[f.key] = vals[f.key] || null;
    out.ts = ts || null;
    out.lang = lang ? lang.toLowerCase() : null;
    return out;
  };

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Player settings"
      description="How the Matterport player behaves when this tour is embedded."
      confirmLabel="Save"
      successMessage="Player settings saved."
      valid={tsOk && langOk}
      submit={(reason) => m.mutateAsync({ vtid, options: options(), reason })}
    >
      <div className="grid max-h-[40vh] grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
        {PLAYER_FIELDS.map((f) => (
          <label key={f.key} className="block space-y-1">
            <span className="text-xs font-medium">{f.label}</span>
            <select className={cls} value={vals[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)}>
              <option value="">Default</option>
              {f.options.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
        ))}
        <label className="block space-y-1">
          <span className="text-xs font-medium">Guided tour starts after (seconds, 0-300)</span>
          <Input type="number" min={0} max={300} placeholder="Default" value={ts} onChange={(e) => set('ts', e.target.value)} />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium">Language (two-letter code)</span>
          <Input maxLength={2} placeholder="Default" value={lang} onChange={(e) => set('lang', e.target.value)} />
        </label>
      </div>
      <p className="text-xs text-gray-500">Applies wherever this tour is embedded on the site.</p>
    </ActionDialog>
  );
};

export const PlayerSettingChips: React.FC<{ options: Record<string, unknown> | null | undefined }> = ({ options }) => {
  const entries = Object.entries(options ?? {}).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([k, v]) => (
        <span key={k} className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-700">
          {k}={String(v)}
        </span>
      ))}
    </div>
  );
};

// ─── Media ───────────────────────────────────────────────────────────────────

const Dimensions: React.FC<{ d: TourDimensions }> = ({ d }) => (
  <div className="overflow-x-auto rounded border border-gray-100">
    <table className="min-w-full text-xs">
      <thead className="bg-gray-50 text-left text-gray-500">
        <tr>
          <th className="px-2 py-1">Floor / room</th>
          <th className="px-2 py-1">Dimensions</th>
          <th className="px-2 py-1">Ceiling</th>
          <th className="px-2 py-1">Net area</th>
          <th className="px-2 py-1">Gross area</th>
        </tr>
      </thead>
      <tbody>
        {d.floors.map((f, i) => (
          <React.Fragment key={i}>
            <tr className="bg-gray-50/60 font-medium">
              <td className="px-2 py-1">{f.floor_label ?? `Floor ${i + 1}`}</td>
              <td />
              <td />
              <td className="px-2 py-1">{f.net_floor_area ?? '—'}</td>
              <td className="px-2 py-1">{f.gross_floor_area ?? '—'}</td>
            </tr>
            {f.rooms.map((r, j) => (
              <tr key={j}>
                <td className="px-2 py-1 pl-5">{r.room ?? '—'}</td>
                <td className="px-2 py-1">{r.dimensions ?? '—'}</td>
                <td className="px-2 py-1">{r.ceiling_height ?? '—'}</td>
                <td className="px-2 py-1">{r.net_floor_area ?? '—'}</td>
                <td className="px-2 py-1">{r.gross_floor_area ?? '—'}</td>
              </tr>
            ))}
          </React.Fragment>
        ))}
        {d.totals && (
          <tr className="border-t font-semibold">
            <td className="px-2 py-1">{d.totals.label ?? 'Total'}</td>
            <td />
            <td />
            <td className="px-2 py-1">{d.totals.net_floor_area ?? '—'}</td>
            <td className="px-2 py-1">{d.totals.gross_floor_area ?? '—'}</td>
          </tr>
        )}
      </tbody>
    </table>
    {d.units && <p className="px-2 py-1 text-[11px] text-gray-400">Units: {d.units}</p>}
  </div>
);

const Section: React.FC<{ title: string; open: boolean; onToggle: () => void; children: React.ReactNode }> = ({
  title,
  open,
  onToggle,
  children,
}) => (
  <div>
    <button type="button" onClick={onToggle} className="flex w-full items-center justify-between text-left" aria-expanded={open}>
      <h4 className="text-sm font-semibold">{title}</h4>
      <span className="text-xs text-blue-600">{open ? 'Hide' : 'Show'}</span>
    </button>
    {open && <div className="mt-3 space-y-3">{children}</div>}
  </div>
);

export const MediaSection: React.FC<{ vtid: number }> = ({ vtid }) => {
  const [open, setOpen] = React.useState(false);
  const { data, isLoading, isError, error } = useVirtualTourMediaQuery(vtid, open);
  const [snaps, setSnaps] = React.useState<string[]>([]);
  const [plans, setPlans] = React.useState<string[]>([]);
  const [importOpen, setImportOpen] = React.useState(false);
  const importM = useImportTourMediaMutation();
  React.useEffect(() => {
    setOpen(false);
    setSnaps([]);
    setPlans([]);
  }, [vtid]);

  const max = data?.max_import_items ?? 0;
  const total = snaps.length + plans.length;
  const toggle = (list: string[], set: (v: string[]) => void, id: string) => {
    if (list.includes(id)) return set(list.filter((x) => x !== id));
    if (max && total >= max) return toast.error(`You can import at most ${max} items at once.`);
    set([...list, id]);
  };

  return (
    <Section title="Media" open={open} onToggle={() => setOpen((o) => !o)}>
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Could not load media.')}</p>
      ) : data ? (
        <>
          <p className="text-xs text-gray-600">
            Photos: {data.images.used} / {data.images.limit === -1 ? 'unlimited' : data.images.limit}.{' '}
            {data.entitlements.auto_floor_plan ? 'The plan includes floor plans.' : 'The plan does not include floor plans.'}
          </p>
          {data.dimensions && <Dimensions d={data.dimensions} />}

          <div>
            <h5 className="mb-1 text-xs font-medium text-gray-700">Snapshots ({data.snapshots.length})</h5>
            {data.errors.snapshots && <p className="text-xs text-gray-400">{data.errors.snapshots}</p>}
            {data.snapshots.length === 0 && !data.errors.snapshots && <p className="text-xs text-gray-500">No snapshots on this model.</p>}
            <div className="grid grid-cols-3 gap-2">
              {data.snapshots.map((s) => (
                <label key={s.id} className="relative block cursor-pointer overflow-hidden rounded border border-gray-200 text-[11px]">
                  {s.thumb ? (
                    <img src={s.thumb} alt={s.label ?? s.filename ?? 'Snapshot'} className="h-20 w-full object-cover" />
                  ) : (
                    <div className="flex h-20 items-center justify-center text-gray-400">no preview</div>
                  )}
                  <input
                    type="checkbox"
                    className="absolute left-1 top-1 h-4 w-4"
                    checked={snaps.includes(s.id)}
                    onChange={() => toggle(snaps, setSnaps, s.id)}
                  />
                  <span className="block truncate px-1 py-0.5 text-gray-600">{s.label || s.filename || s.id}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h5 className="mb-1 text-xs font-medium text-gray-700">Floor plans</h5>
            {data.errors.floorplans && <p className="text-xs text-gray-400">{data.errors.floorplans}</p>}
            {data.floorplans.length === 0 ? (
              <p className="text-xs text-gray-500">No floor plans on this model</p>
            ) : (
              <ul className="space-y-1">
                {data.floorplans.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      disabled={!f.importable}
                      checked={plans.includes(f.id)}
                      onChange={() => toggle(plans, setPlans, f.id)}
                    />
                    <span className="font-mono">{f.id}</span>
                    <span className="uppercase text-gray-500">{f.format ?? ''}</span>
                    {f.colored && <span className="text-gray-500">coloured</span>}
                    {!f.importable && <span className="text-gray-400">format not importable</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <PermissionButton size="sm" code={PERMISSIONS.virtualTour.edit} disabled={total === 0} onClick={() => setImportOpen(true)}>
            Import selected ({total}
            {max ? ` / ${max}` : ''})
          </PermissionButton>
          <ActionDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            title="Import selected media?"
            description={`Copies ${snaps.length} snapshot(s) and ${plans.length} floor plan(s) onto the listing. Import queued; each item's result appears in Errors & Logs.`}
            confirmLabel="Import"
            allowBypass
            bypassLabel="Import anyway (bypass plan limits)"
            successMessage="Import queued; each item's result appears in Errors & Logs."
            submit={async (reason, f) => {
              await importM.mutateAsync({ vtid, snapshot_ids: snaps, floorplan_ids: plans, reason, ...f });
              setSnaps([]);
              setPlans([]);
            }}
          />
        </>
      ) : null}
    </Section>
  );
};

// ─── Add-ons ─────────────────────────────────────────────────────────────────

export const AddonsSection: React.FC<{ vtid: number }> = ({ vtid }) => {
  const [open, setOpen] = React.useState(false);
  const { data, isLoading, isError, error } = useVirtualTourAddonsQuery(vtid, open);
  const [order, setOrder] = React.useState<{ id: string; name: string } | null>(null);
  const orderM = useOrderTourAddonMutation();
  React.useEffect(() => {
    setOpen(false);
    setOrder(null);
  }, [vtid]);

  return (
    <Section title="Add-ons" open={open} onToggle={() => setOpen((o) => !o)}>
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Could not load add-ons.')}</p>
      ) : data ? (
        <>
          <p className="text-xs text-gray-600">
            {data.orders.remaining} of {data.orders.cap} add-on orders left this month
          </p>
          {data.bundles.length === 0 && <p className="text-xs text-gray-500">No add-on bundles available.</p>}
          <ul className="space-y-2">
            {data.bundles.map((b) => (
              <li key={b.id} className="rounded border border-gray-100 px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <span className="font-medium">{b.name ?? b.id}</span>
                    <span className="ml-2 text-gray-500">
                      {b.availability ?? '—'}
                      {b.processing_status ? `, ${b.processing_status}` : ''}
                    </span>
                  </div>
                  {b.availability !== 'unlocked' && (
                    <PermissionButton
                      size="sm"
                      variant="outline"
                      code={PERMISSIONS.virtualTour.purchase}
                      onClick={() => setOrder({ id: b.id, name: b.name ?? b.id })}
                    >
                      Order
                    </PermissionButton>
                  )}
                </div>
                {b.description && <p className="mt-1 text-gray-500">{b.description}</p>}
                {b.failure_reason && <p className="mt-1 text-red-600">{b.failure_reason}</p>}
                {b.assets.some((a) => a.url) && (
                  <ul className="mt-1 space-y-0.5">
                    {b.assets
                      .filter((a) => a.url && /^https?:\/\//i.test(a.url))
                      .map((a, i) => (
                        <li key={i}>
                          <a href={a.url as string} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {a.filename ?? a.format ?? 'Download'}
                          </a>
                        </li>
                      ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <ActionDialog
            open={order != null}
            onClose={() => setOrder(null)}
            title={`Order "${order?.name ?? ''}"?`}
            description={`Paid by FeelyFeely, not the seller. Matterport does not show a price here, and add-ons are not included in our plan, so this may be refused or billed separately. ${data.orders.remaining} of ${data.orders.cap} add-on orders left this month.`}
            confirmLabel="Order add-on"
            successMessage="Add-on ordered."
            submit={(reason) => orderM.mutateAsync({ vtid, bundle_id: order?.id ?? '', reason })}
          />
        </>
      ) : null}
    </Section>
  );
};

